import * as React from "react";
import { User } from "lucide-react";
import { cn } from "@/utils/cn";

const hanCharacterRegex = /\p{Script=Han}/u;
const compoundHanSurnames = new Set([
  "\u6b50\u967d",
  "\u53f8\u99ac",
  "\u8af8\u845b",
  "\u4e0a\u5b98",
  "\u53f8\u5f92",
  "\u5357\u5bae",
  "\u590f\u4faf",
  "\u7687\u752b",
  "\u5c09\u9072",
  "\u516c\u5b6b",
  "\u6155\u5bb9",
  "\u4ee4\u72d0",
  "\u6771\u65b9",
  "\u7aef\u6728",
  "\u7533\u5c60",
  "\u5b87\u6587",
  "\u9577\u5b6b",
  "\u937e\u96e2",
  "\u8ed2\u8f45",
  "\u767e\u91cc",
]);

interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  name?: string; // Add name prop
  size?: "sm" | "md" | "lg" | "xs";
  colorScheme?: "blue" | "purple";
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      src,
      alt,
      name,
      size = "md",
      colorScheme = "purple",
      ...props
    },
    ref,
  ) => {
    const [imageError, setImageError] = React.useState(false);

    React.useEffect(() => {
      setImageError(false); // Reset error when src changes
    }, [src]);

    const getInitials = (name: string) => {
      const trimmedName = name.trim();
      const hanCharacters = Array.from(trimmedName).filter((character) =>
        hanCharacterRegex.test(character),
      );

      if (hanCharacters.length > 0) {
        const compoundSurname = hanCharacters.slice(0, 2).join("");
        return compoundHanSurnames.has(compoundSurname)
          ? compoundSurname
          : hanCharacters[0];
      }

      const names = trimmedName.split(/\s+/).filter(Boolean);
      if (names.length > 1) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }

      return trimmedName.substring(0, 2).toUpperCase();
    };

    const sizeClasses = {
      xs: "h-6 w-6 text-xs font-medium",
      sm: "h-7 w-7 text-sm font-semibold",
      md: "h-10 w-10 text-lg",
      lg: "h-12 w-12 text-xl",
    };

    const colorClasses = {
      blue: "bg-giant-blue/10 text-giant-blue",
      purple: "bg-purple-light-5 text-purple",
    };

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex shrink-0 overflow-hidden rounded-full",
          sizeClasses[size],
          colorClasses[colorScheme],
          className,
        )}
        {...props}
      >
        {src && !imageError ? (
          <img
            className="aspect-square h-full w-full"
            src={src}
            alt={alt}
            onError={() => setImageError(true)}
          />
        ) : name ? (
          <span className="flex h-full w-full items-center justify-center rounded-full">
            {getInitials(name)}
          </span>
        ) : (
          <span className="flex h-full w-full items-center justify-center rounded-full">
            <User className="h-1/2 w-1/2" />
          </span>
        )}
      </div>
    );
  },
);

Avatar.displayName = "Avatar";

export { Avatar };
